import { HighLowChart } from '@toggle/design-system';
import { formatPriceWithCurrency, useElemSize } from '@toggle/helpers';
import { Entity } from '@toggle/toggle';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetEarningById } from '~/hooks/use-get-earning-by-id/useGetEarningById';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useScenarios } from '~/hooks/use-scenarios/useScenarios';
import { TabsType } from '~/modules/tab-content/types';
import { ChartLegend } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/components/earnings-chart/components/chart-legend/ChartLegend';
import { Expectation } from '~/views/dashboard/calendar-widget/calendar-event-drawer/drawer-content/earnings-event-drawer-content/types';

import * as S from './HistoricalReaction.styles';

const CHART_HEIGHT = 300;
const START_HORIZON = '-2d';
const END_HORIZON = '5d';

interface HistoricalReactionProps {
  entity: Entity;
  earningsId: string;
}

export const HistoricalReaction = ({
  entity,
  earningsId,
}: HistoricalReactionProps) => {
  const { t } = useTranslation(['earnings']);
  const chartWrapperRef = useRef(null);
  const { width } = useElemSize(chartWrapperRef);
  const { navigateToPage } = useNavigateToAssetOverview();
  const {
    sendMessage,
    data: scenariosData,
    isLoading: isScenariosLoading,
  } = useScenarios();

  const {
    data: earningsData,
    isLoading,
    isError: isEarningsDataError,
    error: earningsDataError,
  } = useGetEarningById(earningsId);

  const epsSurprise = earningsData?.eps.surprise;
  const expectation =
    epsSurprise !== undefined
      ? epsSurprise >= 0
        ? Expectation.Beat
        : Expectation.Miss
      : undefined;

  const entityName = entity.name_short || entity.name;

  const searchParams = useMemo(() => {
    const params = new URLSearchParams();

    if (earningsData?.eps?.surprise !== undefined && expectation) {
      params.append(
        'epsFilter',
        expectation === Expectation.Beat ? 'beats' : 'misses'
      );
      params.append('epsValue', earningsData.eps.surprise.toString());
    }

    return params;
  }, [earningsData, expectation]);

  const handleDigIntoData = useCallback(() => {
    navigateToPage({
      entityTag: entity.tag,
      tab: TabsType.Earnings,
      searchParams,
    });
  }, [navigateToPage, entity.tag, searchParams]);

  const hasError = isEarningsDataError;
  const errorMessage = earningsDataError?.message;

  const mappedScenariosData = useMemo(() => {
    if (!scenariosData?.length) {
      return [];
    }

    const firstScenario = scenariosData[0];

    return (
      firstScenario?.predictions?.map(item => ({
        high: item.high,
        median: item.median,
        low: item.low,
        label: t(item.horizon),
      })) ?? []
    );
  }, [scenariosData, t]);

  useEffect(() => {
    if (!earningsData || !expectation || isLoading) {
      return;
    }

    const condition = expectation === Expectation.Beat ? 'above' : 'below';
    const value = expectation === Expectation.Beat ? 0.01 : -0.01;

    const payload = {
      entities: [
        {
          tag: entity.tag,
        },
      ],
      conditions: [
        {
          condition: condition,
          snake: `${entity.tag}.surprise_absolute_eps`,
          value: value,
        },
      ],
      start_horizon: START_HORIZON,
      end_horizon: END_HORIZON,
      skip_report: true,
      non_overlapping: true,
    };

    sendMessage(payload);
  }, [earningsData, entity.tag, expectation]);

  if (isLoading || isScenariosLoading) {
    return <S.StyledLoader />;
  }

  if (hasError) {
    throw new Error(errorMessage);
  }

  if (!earningsData || !mappedScenariosData.length || !expectation) {
    return null;
  }

  const valueFormatted = formatPriceWithCurrency(
    earningsData.eps.surprise ?? 0,
    earningsData.currency
  );

  return (
    <S.Root>
      <S.FlexRow>
        <S.ChartTitleLabel>
          {expectation === Expectation.Beat
            ? t(
                'earnings:earningsInsight.drawer.earningsRecap.stockPerformanceAfterEarningsBeats',
                {
                  name: entityName,
                  count: scenariosData[0]?.episodes?.length ?? 0,
                  value: valueFormatted,
                }
              )
            : t(
                'earnings:earningsInsight.drawer.earningsRecap.stockPerformanceAfterEarningsMisses',
                {
                  name: entityName,
                  count: scenariosData[0]?.episodes?.length ?? 0,
                  value: valueFormatted,
                }
              )}
        </S.ChartTitleLabel>
        <S.StyledButton
          label={t(
            'earnings:earningsInsight.drawer.earningsPreview.digIntoTheData'
          )}
          variant="empty"
          onClick={handleDigIntoData}
        />
      </S.FlexRow>
      <ChartLegend />
      <div ref={chartWrapperRef} data-testid="chart-container">
        <HighLowChart
          data={mappedScenariosData}
          width={width}
          height={CHART_HEIGHT}
          variant="secondary"
        />
      </div>
    </S.Root>
  );
};
